<template>
  <component
    role="button"
    aria-pressed="false"
    :is="comType"
    v-bind="comAttrs"
    :class="rootClasses"
    v-if="fields && $appVisible($route, fields) && btnText"
    @touchstart="onTouchStart"
    @mouseup="onTouchStart"
    @click="onClick"
  >
    <div class="e-site-button__loading" v-if="loading">
      <spinner name="ball-beat" size="small" />
    </div>
    <template v-else>
      <span class="e-site-button__text" v-html="btnText" />
      <slot class="e-site-button__icon" v-if="$slots.icon" />
      <Icon
        class="e-site-button__icon"
        :field="fields.icon"
        v-else-if="!isNullOrEmpty(fields.icon?.value.svgCode)"
      />
    </template>
  </component>
</template>

<script>
import { computed, reactive, toRefs, watch, getCurrentInstance, inject } from 'vue';
import { settingValue } from '@/utils/site-utils';
import { debounce, merge } from 'lodash';
import { isNullOrEmpty } from '@/utils/obj-utils';
import { sitecoreProps } from '@/utils/constants';
import { gtmPush } from '@/utils/gtm-utils';
import { qtUtils } from '@/utils/ali-tracker-utils';
import * as querystring from 'querystring';
import { ifEmpty } from '@/utils/string-utils';
import { openWindow } from '@/utils/dom-utils';
import { getBetterUrl } from '@/utils/uri-utils';

export default {
  name: 'SiteButton',
  emits: ['click'],
  props: {
    /**@type ButtonField*/
    fields: {
      type: Object,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hasAnimation: {
      type: Boolean,
      default: true,
    },
    icon: {
      type: String,
      default: null,
    },
    size: {
      type: String,
      default: null,
    },
    qtData: {
      type: Object,
    },
    moduleName: {
      type: String,
    },
    /**
     * Whether to allow the button to handle link navigation internally.
     * When `true` (default): if a link is configured, renders as site-link and navigates normally.
     * When `false`: forces rendering as a plain button, link navigation is suppressed entirely.
     *   The parent component can listen to the `@click` event and handle navigation itself.
     */
    openLink: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether to allow the button to handle file downloads internally.
     * When `true` (default): if a file is configured, clicking downloads it normally.
     * When `false`: file download is suppressed entirely.
     *   The parent component can listen to the `@click` event and handle the download itself.
     */
    openDownload: {
      type: Boolean,
      default: true,
    },
    ...sitecoreProps,
  },
  setup(props, ctx) {
    let touchTimeout, clickTriggered;
    const { proxy } = getCurrentInstance();
    const { $jss } = proxy;
    const _methods = {
      getTheme: () => {
        let result = settingValue(props.fields?.theme);
        const type = _methods.getType();
        if (type === 'primary' && !result) {
          result = 'green';
        }
        return result;
      },
      getType: () => settingValue(props.fields?.type, 'primary'),
      getSize: () => settingValue(props.fields?.size, 'medium'),
      triggerFileDownload() {
        const src = props.fields?.file?.value?.src;
        if (!src) return;
        const modeview = props.fields?.modeview?.value;
        if (modeview) {
          window.open(src, '_blank');
          return;
        }
        const link = document.createElement('a');
        link.href = src;
        link.download = props.fields?.file?.value?.name || 'download';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        const handleDownloadComplete = () => {
          if (document.body.contains(link)) document.body.removeChild(link);
          window.removeEventListener('focus', handleDownloadComplete);
        };
        window.addEventListener('focus', handleDownloadComplete);
        setTimeout(() => {
          if (document.body.contains(link)) document.body.removeChild(link);
          window.removeEventListener('focus', handleDownloadComplete);
        }, 5000);
      },
      qtTrackClick() {
        /**@type SitePage */
        const page = $jss.routeData();
        const { aliQtEnabled } = props.fields || {};
        if (aliQtEnabled?.value) {
          const qtEventCode = qtUtils.getBtnCode(page, props);
          if (isNullOrEmpty(qtEventCode)) return;
          const qtParams = methods.getTrackParams();
          qtUtils.trackClick(qtEventCode, qtParams);
        }
      },
      clicking: debounce((e) => {
        if (props.disabled || props.loading) return false;
        _methods.qtTrackClick();
        gtmPush({
          event: `button_${state.type}_click`,
        });
        ctx.emit('click', e);
        const fileSrc = props.fields?.file?.value?.src;
        if (props.openDownload && fileSrc) {
          _methods.triggerFileDownload();
          return { handled: true };
        }

        const contactId = props.fields?.contactId?.value;
        if (contactId) {
          if (props.openLink && props.fields?.link?.value?.href) {
            return { handled: false };
          }
          const anchor = document.getElementById(contactId);
          if (anchor) {
            const offset = anchor.getBoundingClientRect();
            window.scrollTo(0, window.scrollY + offset.top - 80);
          }
          return { handled: true };
        }

        const { anchorId } = props.fields || {};
        if (!anchorId?.value) return { handled: false };
        const anchor = document.getElementById(anchorId?.value);
        if (!anchor) return { handled: false };
        const offset = anchor.getBoundingClientRect();
        window.scrollTo(0, offset.top);
        return { handled: true };
      }, 150),
    };
    const state = reactive({
      theme: _methods.getTheme(),
      type: _methods.getType(),
      size: _methods.getSize(),
      startY: 0,
    });
    const computes = {
      btnText: computed(() =>
        ifEmpty(props?.fields?.text?.value, props?.fields?.link?.value?.text)
      ),
      comType: computed(() => {
        if (!props.openLink) return 'button';
        return isNullOrEmpty(props?.fields?.link?.value?.href) ? 'button' : 'site-link';
      }),
      comAttrs: computed(() => {
        if (props.openLink && !isNullOrEmpty(props?.fields?.link?.value?.href)) {
          const { value } = props?.fields?.link;
          let { href } = value;
          if (/http:\/\/(mailto|tel):/gi.test(href)) {
            href = href.replace(/http:\/\//gi, '');
          }
          const contactId = props?.fields?.contactId?.value;
          if (contactId) {
            href = href.includes('#') ? href : `${href}#${contactId}`;
          }
          return merge({}, ctx.attrs, {
            link: {
              value: {
                ...value,
                href,
                text: computes.btnText.value,
              },
            },
          });
        }
        return ctx.attrs;
      }),
      rootClasses: computed(() => {
        let size = props.size ?? settingValue(props.fields.size, 'medium');
        return [
          'e-site-button',
          state.type,
          state.size,
          `theme-${state.theme}`,
          {
            disabled: props.disabled,
            [`size-${size}`]: !isNullOrEmpty(size) && size !== 'medium',
            reverse: props?.fields?.reverse?.value ?? false,
            fullWidthOnMobile: props?.fields?.fullWidthOnMobile?.value ?? false,
            loading: props.loading,
            radius: props?.fields?.radius?.value ?? false,
          },
        ];
      }),
    };
    const methods = {
      merge,
      onClick: debounce((e) => {
        if (clickTriggered) return false;
        if (props.openDownload && props.fields?.file?.value?.src) e.preventDefault();
        // if (!computes.isSafari.value) {
        _methods.clicking(e);
        // }
      }, 110),
      onTouchStart: debounce((e) => {
        if (!computes?.isSafari?.value) return;
        if (clickTriggered) return;
        clickTriggered = true;
        const startRect = e.target.getBoundingClientRect();
        const startY = startRect.top;
        if (touchTimeout) clearTimeout(touchTimeout);
        touchTimeout = setTimeout(() => {
          clickTriggered = false;
          const endRect = e.target.getBoundingClientRect();
          if (Math.abs(endRect.top - startY) > 50) return;
          e.preventDefault();
          const result = _methods.clicking(e);
          if (result?.handled) return;
          const fileSrc = props.fields?.file?.value?.src;
          if (props.openDownload && fileSrc) {
            _methods.triggerFileDownload();
            return;
          }
          const { href, target } = props.fields?.link?.value || {};
          if (props.openLink && !isNullOrEmpty(href)) {
            const url = getBetterUrl(href, null, true);
            openWindow(url, target);
          }
        }, 420);
      }, 100),
      onMouseup: debounce((e) => {
        if (clickTriggered) return false;
        if (!computes?.isSafari?.value) return;
        e.preventDefault();
        const result = _methods.clicking(e);
        if (result?.handled) return false;
        const fileSrc = props.fields?.file?.value?.src;
        if (props.openDownload && fileSrc) {
          _methods.triggerFileDownload();
          return false;
        }
        const { href, target } = props.fields?.link?.value || {};
        if (props.openLink && !isNullOrEmpty(href)) {
          const url = getBetterUrl(href, null, true);
          openWindow(url, target);
        }
        return false;
      }, 100),
      getTrackParams() {
        const { aliQtParams } = props.fields || {};
        const qtParams = {
          button_id: props.id ?? props?.fields?.link?.value.href,
          button_name: props.name ?? props?.fields?.link?.value.href,
          customized_button_name: props.fields?.text.value,
        };
        if (!isNullOrEmpty(aliQtParams?.value)) {
          merge(qtParams, querystring.parse(aliQtParams?.value));
        }
        merge(qtParams, props.qtData);
        return qtParams;
      },
    };
    watch(
      () => props.fields,
      (n) => {
        state.theme = _methods.getTheme();
        state.type = _methods.getType();
      },
      {
        deep: true,
      }
    );
    return {
      ...toRefs(state),
      ...computes,
      ...methods,
      isNullOrEmpty,
    };
  },
};
</script>

<style lang="scss">
@import '../styles/variables';
@import '../styles/functions';
@import '../styles/mixins';

.e-site-button {
  $c: &;
  width: fit-content;
  height: 48px;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  gap: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  line-height: 1;
  border-radius: 4px;
  cursor: pointer;
  color: currentColor;
  background: transparent;
  user-select: none;
  transition-timing-function: cubic-bezier(0.38, 0.015, 0, 0.995);
  outline: none;
  &:focus {
    outline: none;
  }
  &:active {
    outline: none;
  }
  &__text {
    line-height: 1;
  }
  &__icon {
    transition-timing-function: cubic-bezier(0.38, 0.015, 0, 0.995);
    svg {
      height: 16px;
    }
    // Force icon fill/stroke to follow the button text color so they
    // change in sync on hover (overrides baked-in SVG attributes).
    [fill]:not([fill='none']) {
      fill: currentColor;
    }
    [stroke]:not([stroke='none']) {
      stroke: currentColor;
    }
  }
  &.large {
    height: 54px;
    font-size: 18px;
    line-height: 22px;
    padding: 16px 55px;
  }
  &.medium {
    height: 48px;
    font-size: 16px;
    line-height: 22px;
    padding: 13px 33px;
  }
  &.small {
    height: 32px;
    font-size: 14px;
    line-height: 18px;
    padding: 7px 17px;
  }
  &.primary {
    background-color: currentColor;
    transition: background-color 0.3s;
    &.theme {
      &-green {
        color: $white;
        background: $blue-night;
        &:hover {
          background: $blue-night;
        }
        &:active,
        &:focus {
          color: $white;
        }
        &.disabled {
          border-color: $neutral-disable-surface !important;
          background: $neutral-disable-surface !important;
          color: #b4b4b4 !important;
        }
      }
      &-grey {
        color: #314158;
        background: #f1f5f9;
        &:hover {
          background: $blue-night;
          color: $white;
        }
        &:active,
        &:focus {
          color: $white;
        }
        &.disabled {
          border-color: $neutral-disable-surface !important;
          background: $neutral-disable-surface !important;
          color: #b4b4b4 !important;
        }
      }
      &-white {
        color: $blue-night;
        background: $white;
        &.disabled {
          border-color: #8fa690 !important;
          background: #8fa690 !important;
          color: rgba(255, 255, 255, 0.4) !important;
        }
      }
    }
  }
  &.secondary {
    background-color: transparent;
    &.theme {
      &-green {
        border-color: $blue-night;
        color: $blue-night;
        &.disabled {
          border-color: #b4b4b4 !important;
          color: #b4b4b4 !important;
        }
      }
      &-grey {
        border-color: #314158;
        color: #314158;
        &.disabled {
          border-color: $neutral-disable-surface !important;
          color: #b4b4b4 !important;
        }
      }
      &-white {
        border-color: $white;
        color: $white;
        &.disabled {
          border-color: rgba(255, 255, 255, 0.6) !important;
          color: rgba(255, 255, 255, 0.6) !important;
        }
      }
    }
  }
  &.tertiary {
    padding: 0;
    height: auto;
    gap: 8px;
    justify-content: flex-start;
    transition: color 0.3s;
    color: currentColor;
    background-color: transparent;
    border-color: transparent;
    &.theme {
      &-green {
        color: $blue-night;
        &.disabled {
          color: #b4b4b4 !important;
        }
      }
      &-grey {
        color: #314158;
        &.disabled {
          color: #b4b4b4 !important;
        }
      }
      &-white {
        color: $white;
        &:hover {
          color: $blue-night;
        }
        &:active,
        &:focus {
          color: $blue-night;
        }
        &.disabled {
          color: rgba(255, 255, 255, 0.4) !important;
        }
      }
    }
    #{$c}__icon {
      transform: translateX(0);
    }
    &.disabled {
      color: rgba($grey-next, 0.3) !important;
    }
  }
  &.reverse {
    gap: 8px;
    #{$c}__text {
      order: 2;
    }
  }
  &.loading {
    padding: 0 48px;
    &.size- {
      &small {
        padding: 0 28px;
      }
    }
  }
  &.fullWidthOnMobile {
    width: 100%;
  }
  &.radius {
    border-radius: 32px;
  }
  @include tablet-landscape {
    &.fullWidthOnMobile {
      width: fit-content;
    }
  }
  @include desktop {
    &.tertiary {
      gap: 8px;
      #{$c}__icon {
        opacity: 1;
      }
    }

    &.size- {
      &small {
        gap: 4px;
        #{$c}__icon {
          svg {
            width: 12px;
            height: 12px;
          }
        }
        &.tertiary {
          gap: 8px;
        }
      }
    }
    &.reverse {
      #{$c}__icon {
        opacity: 1;
      }
      &.primary,
      &.secondary {
        padding: 0 24px;
      }
      &.size- {
        &small {
          &.primary,
          &.secondary {
            padding: 0 12px;
          }
        }
      }
      &.loading {
        padding: 0 48px;
        &.size- {
          &small {
            padding: 0 28px;
          }
        }
      }
    }
  }
}
html.rtl {
  .e-site-button {
    $c: '.e-site-button';
    direction: rtl;
    &.primary,
    &.secondary {
      padding: 0 24px;
    }
    &.size- {
      &small {
        &.primary,
        &.secondary {
          padding: 0 12px;
        }
      }
    }
    &.reverse {
      &.primary,
      &.secondary {
        padding: 0 24px;
      }
      &.size- {
        &small {
          &.primary,
          &.secondary {
            padding: 0 12px;
          }
        }
      }
      &.loading {
        padding: 0 48px;
        &.size- {
          &small {
            padding: 0 28px;
          }
        }
      }
    }
  }
}
</style>
